import { Component, inject, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { AccountKind, TransactionsRangeFilters, TransactionsTypeFilters } from '@inyova/models';
import { getTypeFiltersForKind } from '@inyova/utils';
import { IonModal, ModalController } from '@ionic/angular';
import * as dayjs from 'dayjs';
import { Dayjs } from 'dayjs';

@Component({
  selector: 'app-account-transactions-filter-modal',
  templateUrl: './account-transactions-filter-modal.component.html',
  styleUrls: ['./account-transactions-filter-modal.component.scss'],
  standalone: false
})
export class AccountTransactionsFilterModalComponent implements OnInit {
  @Input() rangeFilter: TransactionsRangeFilters;
  @Input() selectedTypeFilters: TransactionsTypeFilters[];
  @Input() customRange: { from: null | string; to: null | string };
  @Input() language: string;
  @Input() kind: AccountKind;

  @ViewChild('fromModal', { static: false }) fromModal: IonModal;
  @ViewChild('toModal', { static: false }) toModal: IonModal;

  private modalController: ModalController = inject(ModalController);
  private fb: FormBuilder = inject(FormBuilder);

  dateRangeFilters: TransactionsRangeFilters[] = Object.values(TransactionsRangeFilters);
  typeFilters: TransactionsTypeFilters[];
  transactionsRangeFilters = TransactionsRangeFilters;
  now: Dayjs = dayjs();
  toMin: string;
  fromMax: string;
  toMax = this.now.format('YYYY-MM-DD');

  form: FormGroup = this.fb.group({
    from: [this.now.format('DD/MM/YYYY')],
    to: [this.now.format('DD/MM/YYYY')]
  });

  get fromControl() {
    return this.form.get('from');
  }

  get toControl() {
    return this.form.get('to');
  }

  ngOnInit(): void {
    this.typeFilters = this.getTypeFiltersBasedOnAccountKind(Object.values(TransactionsTypeFilters), this.kind);

    if (this.rangeFilter === TransactionsRangeFilters.CUSTOM_RANGE && this.customRange.from !== null) {
      this.fromControl.setValue(dayjs(this.customRange.from).format('DD/MM/YYYY'));
      this.toControl.setValue(dayjs(this.customRange.to).format('DD/MM/YYYY'));
      this.fromMax = this.customRange.to;
      this.toMin = this.customRange.from;
      return;
    }

    this.fromMax = this.now.format('YYYY-MM-DD');
  }

  onDismiss() {
    void this.modalController.dismiss();
  }

  onValueChange($event, fromCalendar: boolean) {
    if (typeof $event.detail !== 'undefined') {
      if (fromCalendar) {
        this.fromControl.setValue(dayjs($event.detail.value).format('DD/MM/YYYY'));
        const from = dayjs($event.detail.value).format('YYYY-MM-DD');
        this.toMin = from;
        this.customRange = { ...this.customRange, from };
        return;
      }
      this.toControl.setValue(dayjs($event.detail.value).format('DD/MM/YYYY'));
      const to = dayjs($event.detail.value).format('YYYY-MM-DD');
      this.fromMax = to;
      this.customRange = { ...this.customRange, to };
    }
  }

  onRangeSelect(filter: string | number) {
    this.rangeFilter = filter as TransactionsRangeFilters;
    if (filter === TransactionsRangeFilters.CUSTOM_RANGE && this.customRange.from === null && this.customRange.to === null) {
      this.customRange = {
        from: this.now.format('YYYY-MM-DD'),
        to: this.now.format('YYYY-MM-DD')
      };
    }
  }

  onTypeSelect(filter: string | number) {
    const selected = filter as TransactionsTypeFilters;

    if (selected === TransactionsTypeFilters.ALL) {
      this.selectedTypeFilters = [];
      return;
    }

    const isFilterAlreadySelected = this.selectedTypeFilters.includes(selected);
    this.selectedTypeFilters = isFilterAlreadySelected
      ? this.selectedTypeFilters.filter((item) => item !== selected)
      : [...this.selectedTypeFilters, selected];
  }

  getTypeFiltersBasedOnAccountKind(filters: TransactionsTypeFilters[], kind: AccountKind) {
    return getTypeFiltersForKind(filters, kind);
  }

  async onInputClick(fromCalendar = false) {
    if (fromCalendar) {
      await this.fromModal.present();
    } else {
      await this.toModal.present();
    }
  }

  onFiltersApply() {
    void this.modalController.dismiss({
      rangeFilter: this.rangeFilter,
      selectedTypeFilters: this.selectedTypeFilters,
      customRange: this.customRange
    });
  }

  onFilterClear() {
    void this.modalController.dismiss({
      rangeFilter: TransactionsRangeFilters.ALL,
      selectedTypeFilters: [],
      customRange: { from: null, to: null }
    });
  }
}
